<script>
export default {
  name: "AgentSelectorComponent",
  props: {
    value: {
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    agencySelectOptions: {
      type: Array,
      default: () => [],
    },
    selectedAgencyColorStyle: {
      type: Object,
      default: () => ({}),
    },
    canRequestAgencyChange: {
      type: Boolean,
      default: false,
    },
    selectedAgencyName: {
      type: String,
      default: "",
    },
    activeAgencyName: {
      type: String,
      default: "Agence non sélectionnée",
    },
    pendingAgencyName: {
      type: String,
      default: "Agence non sélectionnée",
    },
    // the agency the catalogue and the cart are on now (the selector highlights it)
    activeAgencyId: {
      default: null,
    },
    // switching archives the current cart: confirm only when it has lines
    cartHasItems: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showChangeAgencyConfirmModal: false,
    };
  },
  computed: {
    pendingSelectedAgency: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    isActiveAgency(option) {
      return this.activeAgencyId != null && String(option.value) === String(this.activeAgencyId);
    },
    // one click: pick the agency, then confirm in the modal only if the cart has lines (they get archived)
    selectAgency(option) {
      if (this.isActiveAgency(option)) {
        return;
      }
      this.pendingSelectedAgency = option.value;
      this.$nextTick(() => {
        if (this.cartHasItems) {
          this.openChangeAgencyConfirmModal();
        } else {
          this.$emit("confirm");
        }
      });
    },
    openChangeAgencyConfirmModal() {
      if (!this.canRequestAgencyChange) {
        return;
      }
      this.showChangeAgencyConfirmModal = true;
    },
    confirmAgencyChange() {
      this.showChangeAgencyConfirmModal = false;
      this.$emit("confirm");
    },
    cancelAgencyChange() {
      this.showChangeAgencyConfirmModal = false;
      this.$emit("cancel");
    },
  },
};
</script>

<template>
  <div v-if="visible" class="row pl-0 pl-sm-4 pr-sm-4 d-print-none">
    <div class="col-12">
      <div class="agency-card" :style="selectedAgencyColorStyle">
        <div class="agency-card__intro">
          <span class="agency-card__icon"><i class="bx bx-buildings" aria-hidden="true"></i></span>
          <div class="agency-card__text">
            <div class="agency-card__title">Agence de commande</div>
            <div class="agency-card__copy">Les articles affichés correspondent à l'agence sélectionnée.</div>
          </div>
        </div>
        <div class="agency-card__segments" role="radiogroup" aria-label="Agence de commande">
          <button
            v-for="option in agencySelectOptions"
            :key="option.value"
            type="button"
            role="radio"
            class="agency-card__segment"
            :class="{ 'is-active': isActiveAgency(option) }"
            :style="{ '--seg-rgb': option.rgb || '0, 122, 51' }"
            :aria-checked="isActiveAgency(option) ? 'true' : 'false'"
            :title="isActiveAgency(option) ? 'Agence actuelle' : 'Passer sur l\'agence ' + option.text"
            @click="selectAgency(option)"
          >
            <i v-if="isActiveAgency(option)" class="bx bx-check-circle" aria-hidden="true"></i>
            <span class="agency-card__name">{{ option.text }}</span>
          </button>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showChangeAgencyConfirmModal"
      centered
      hide-header
      hide-footer
      body-class="agency-confirm-modal__body"
      dialog-class="agency-confirm-modal__dialog"
      content-class="agency-confirm-modal__content"
      @hidden="cancelAgencyChange"
    >
      <button type="button" class="agency-confirm__close" aria-label="Fermer" @click="cancelAgencyChange">
        <i class="bx bx-x" aria-hidden="true"></i>
      </button>
      <div class="agency-confirm__warning" aria-hidden="true">
        <i class="bx bx-error" aria-hidden="true"></i>
      </div>
      <h4 class="agency-confirm__title">Changer d'agence ?</h4>
      <p class="agency-confirm__text">
        Vous êtes actuellement sur l'agence <strong>{{ activeAgencyName }}</strong>.<br>
        Voulez-vous vraiment changer pour l'agence <strong>{{ pendingAgencyName }}</strong> ?
      </p>
      <div class="agency-confirm__notice">
        <i class="bx bx-info-circle" aria-hidden="true"></i>
        <span>Les articles affichés seront mis à jour en fonction de la nouvelle agence sélectionnée. Votre panier actuel sera archivé.</span>
      </div>
      <div class="agency-confirm__actions">
        <b-button variant="light" class="agency-confirm__cancel" @click="cancelAgencyChange">Annuler</b-button>
        <b-button variant="success" class="agency-confirm__confirm" @click="confirmAgencyChange">
          <i class="bx bx-check" aria-hidden="true"></i>
          Oui, changer
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.agency-card { --agency-rgb: 0, 122, 51; display: flex; align-items: center; justify-content: space-between; gap: 1.25rem; margin: 0 0 1rem; padding: .85rem 1.1rem; border: 1px solid rgba(var(--agency-rgb), .22); border-radius: 10px; background: linear-gradient(90deg, rgba(var(--agency-rgb), .10) 0%, rgba(var(--agency-rgb), .04) 60%, rgba(var(--agency-rgb), .12) 100%); box-shadow: 0 8px 20px rgba(16, 24, 40, .06); transition: background .25s, border-color .25s; }
.agency-card__intro { display: flex; align-items: center; gap: .85rem; min-width: 0; }
.agency-card__icon { display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; background: #fff; color: rgb(var(--agency-rgb)); font-size: 26px; box-shadow: 0 2px 6px rgba(16, 24, 40, .08); transition: color .25s; }
.agency-card__title { color: #101828; font-size: 15px; font-weight: 700; line-height: 1.25; }
.agency-card__copy { margin-top: .15rem; color: #475467; font-size: 13px; line-height: 1.4; }
.agency-card__segments { display: inline-flex; flex: 0 0 auto; gap: .25rem; padding: .25rem; border: 1px solid rgba(16, 24, 40, .08); border-radius: 12px; background: #fff; box-shadow: inset 0 1px 2px rgba(16, 24, 40, .05); }
.agency-card__segment { --seg-rgb: 0, 122, 51; display: inline-flex; align-items: center; justify-content: center; gap: .45rem; min-width: 130px; min-height: 40px; padding: 0 1.1rem; border: 0; border-radius: 9px; background: transparent; color: #344054; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .2s, color .2s, box-shadow .2s; }
.agency-card__segment:hover:not(.is-active) { background: rgba(var(--seg-rgb), .08); color: rgb(var(--seg-rgb)); }
.agency-card__segment:focus-visible { outline: 2px solid rgba(var(--seg-rgb), .55); outline-offset: 2px; }
.agency-card__segment.is-active { background: rgb(var(--seg-rgb)); color: #fff; font-weight: 700; box-shadow: 0 4px 10px rgba(var(--seg-rgb), .3); cursor: default; }
.agency-card__segment .bx { font-size: 18px; }
.agency-card__name { white-space: nowrap; }
@media (max-width: 767.98px) {
  .agency-card { flex-direction: column; align-items: stretch; gap: .8rem; padding: .85rem; }
  .agency-card__icon { width: 38px; height: 38px; font-size: 22px; }
  .agency-card__segments { display: flex; }
  .agency-card__segment { flex: 1 1 0; min-width: 0; padding: 0 .5rem; }
}
.agency-confirm__close { position: absolute; top: .8rem; right: .8rem; display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 0; border-radius: 50%; background: transparent; color: #475467; font-size: 22px; cursor: pointer; }
.agency-confirm__close:hover { background: #f2f4f7; color: #101828; }
.agency-confirm__warning { display: flex; align-items: center; justify-content: center; width: 58px; height: 58px; margin: 0 auto .85rem; border: 8px solid #fff1d8; border-radius: 50%; background: #fff7ed; color: #ff9f1c; }
.agency-confirm__warning .bx { font-size: 32px; line-height: 1; }
.agency-confirm__title { margin: 0 0 .45rem; text-align: center; text-transform: uppercase; margin-bottom: 20px; color: #101828; font-size: 20px; font-weight: 800; line-height: 1.2; }
.agency-confirm__text { margin: 0 auto 1rem; max-width: 400px; color: #667085; font-size: 13px; font-weight: 500; line-height: 1.55; }
.agency-confirm__text strong { color: #101828; font-weight: 800; text-transform: uppercase; }
.agency-confirm__notice { display: flex; align-items: flex-start; gap: .65rem; margin: 0 0 1.2rem; padding: .75rem .85rem; border-radius: 8px; background: #eaf3ff; color: #1d64d8; font-size: 12px; font-weight: 600; line-height: 1.4; text-align: left; }
.agency-confirm__notice .bx { flex: 0 0 auto; margin-top: .05rem; font-size: 18px; }
.agency-confirm__actions { display: grid; grid-template-columns: 1fr 1.25fr; gap: .75rem; }
.agency-confirm__cancel, .agency-confirm__confirm { min-height: 42px; border-radius: 6px; font-size: 13px; font-weight: 800; }
.agency-confirm__cancel { border: 1px solid #98a2b3; background: #fff; color: #344054; }
.agency-confirm__confirm { display: inline-flex; align-items: center; justify-content: center; gap: .45rem; border-color: #009b57; background: #009b57; color: #fff; }
.agency-confirm__confirm .bx { font-size: 18px; }
@media (max-width: 575.98px) {
  .agency-confirm__actions { grid-template-columns: 1fr; }
}
</style>

<style>
.agency-confirm-modal__dialog { max-width: 430px; }
.agency-confirm-modal__content { overflow: hidden; border: 0; border-radius: 12px; box-shadow: 0 20px 48px rgba(16, 24, 40, .24); }
.agency-confirm-modal__body { position: relative; padding: 1.75rem 1.75rem 1.45rem; text-align: center; }
@media (max-width: 575.98px) {
  .agency-confirm-modal__body { padding: 1.65rem 1.15rem 1.15rem; }
}
</style>